import { ApolloError, gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import React, { MutableRefObject, useCallback } from 'react';
import { useAuth } from '~auth/useAuth';
import { ErrorAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useBulkUnpairZteEnrolmentsMutation } from './__generated__/UnpairdeviceModal.graphql';

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: () => Promise<void> | void;
  enrolmentIds: string[];
}

export function UnpairDevicesModal({ isOpen, onCancel, onSuccess, enrolmentIds }: Props) {
  const initialFocusRef = React.useRef<HTMLButtonElement | null>(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <UnpairDevicesModalContent
          initialFocusRef={initialFocusRef}
          onCancel={onCancel}
          onSuccess={onSuccess}
          enrolmentIds={enrolmentIds}
        />
      </ModalContent>
    </Modal>
  );
}

type ContentProps = {
  initialFocusRef: MutableRefObject<HTMLButtonElement | null>;
  onCancel: () => void;
  onSuccess: () => Promise<void> | void;
  enrolmentIds: string[];
};

function UnpairDevicesModalContent({
  initialFocusRef,
  onCancel,
  onSuccess,
  enrolmentIds,
}: ContentProps) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const isUnpairingAllowed = verifyUserPermissions([Permission.ZteEnrolmentUnpair]);

  const validatedEnrolmentIds = React.useMemo(() => {
    if (!enrolmentIds || !Array.isArray(enrolmentIds)) return [];
    return enrolmentIds.filter((id) => typeof id === 'string' && id.length > 0);
  }, [enrolmentIds]);

  const [bulkUnpairZteEnrolments, { loading: isUnpairing }] = useBulkUnpairZteEnrolmentsMutation();

  const handleUnpair = useCallback(async () => {
    try {
      if (validatedEnrolmentIds.length === 0) {
        throw new Error('No valid enrolment IDs provided');
      }

      const { data } = await bulkUnpairZteEnrolments({
        variables: {
          input: {
            enrolmentIds: validatedEnrolmentIds,
          },
        },
      });

      if (!data?.bulkUnpairZteEnrolments) {
        throw new Error(t('noServerResponse'));
      }

      const { enrolments } = data.bulkUnpairZteEnrolments;
      if (!enrolments || enrolments.length === 0) {
        throw new Error(t('noEnrolmentsUnpaired'));
      }

      toast({
        status: 'success',
        title: t('unpairDevicesSuccess'),
        description: t('devicesSuccessfullyUnpaired', { count: enrolments.length }),
      });

      await onSuccess?.();
    } catch (err) {
      const error = err as ApolloError;

      toast({
        status: 'error',
        title: t('unpairDevicesError'),
        description: fromError(error, 'UnpairDevices', {
          ZTE_ENROLMENT_NOT_FOUND: t('EnrolmentNotFound'),
        }),
      });
    }
  }, [toast, bulkUnpairZteEnrolments, onSuccess, validatedEnrolmentIds]);

  return (
    <>
      <ModalHeader>{t('unpairSelectedDevices')}</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <VStack spacing={4} align="stretch">
          {!isUnpairingAllowed && <ErrorAlert>{t('youDoNotHavePermission')}</ErrorAlert>}

          <Text>{t('unpairDevicesConfirmation')}</Text>

          {isUnpairing && (
            <VStack spacing={4} align="center" py={4}>
              <Spinner size="lg" />
              <Text>{t('unpairingDevices')}</Text>
            </VStack>
          )}
        </VStack>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" onClick={onCancel} isDisabled={isUnpairing}>
          {t('cancel')}
        </Button>
        <Button
          variant="solid"
          colorScheme="red"
          marginLeft="3"
          onClick={handleUnpair}
          isDisabled={isUnpairing || validatedEnrolmentIds.length === 0 || !isUnpairingAllowed}
          isLoading={isUnpairing}
          ref={initialFocusRef}
        >
          {t('unpair')}
        </Button>
      </ModalFooter>
    </>
  );
}

UnpairDevicesModal.mutations = {
  bulkUnpairZteEnrolments: gql`
    mutation bulkUnpairZteEnrolments($input: ZteBulkUnpairInput!) {
      bulkUnpairZteEnrolments(input: $input) {
        enrolments {
          id
        }
      }
    }
  `,
};
